import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Hand, MousePointer2, Eye, Smile, Keyboard, User, ScanEye, LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import { MainLayout } from "@/components/layout/MainLayout";
import { useCaregiverTranslation } from "@/hooks/useCaregiverTranslation";
import { getUserSettings, saveUserSettings, type UserSettings } from "@/lib/userSettings";
import { useFeature } from "@/hooks/useFeature";

interface ControlMethod {
  id: string;
  icon: LucideIcon;
  featureFlag: string;
  compositeIcons?: LucideIcon[];
}

// Control methods - ordered according to design (RTL: right to left)
// Row 1: eye_control, external_switch, gaze_tracking
// Row 2: head_tracking, mouth_gestures, standard_pointer
const allControlMethods: ControlMethod[] = [
  { id: "eye_control", icon: ScanEye, featureFlag: "eyeControl" },
  { id: "external_switch", icon: Keyboard, featureFlag: "externalSwitch" },
  { id: "gaze_tracking", icon: Eye, featureFlag: "gazeTracking" },
  { id: "head_tracking", icon: User, featureFlag: "headTracking" },
  { id: "mouth_gestures", icon: Smile, featureFlag: "mouthGestures" },
  { id: "standard_pointer", icon: MousePointer2, featureFlag: "standardPointer", compositeIcons: [MousePointer2, Hand] },
];

export default function Control() {
  const navigate = useNavigate();
  const { t, dir } = useCaregiverTranslation();
  const [selectedMethod, setSelectedMethod] = useState("standard_pointer");
  const [userGender, setUserGender] = useState<UserSettings["gender"]>("neutral");

  // Check which features are enabled
  const isEyeControlEnabled = useFeature("eyeControl");
  const isExternalSwitchEnabled = useFeature("externalSwitch");
  const isGazeTrackingEnabled = useFeature("gazeTracking");
  const isHeadTrackingEnabled = useFeature("headTracking");
  const isMouthGesturesEnabled = useFeature("mouthGestures");

  // Filter control methods based on feature flags
  const controlMethods = useMemo(() => {
    const featureMap: Record<string, boolean> = {
      eyeControl: isEyeControlEnabled,
      externalSwitch: isExternalSwitchEnabled,
      gazeTracking: isGazeTrackingEnabled,
      headTracking: isHeadTrackingEnabled,
      mouthGestures: isMouthGesturesEnabled,
      standardPointer: true, // Always enabled
    };
    return allControlMethods.filter(method => featureMap[method.featureFlag] !== false);
  }, [isEyeControlEnabled, isExternalSwitchEnabled, isGazeTrackingEnabled, isHeadTrackingEnabled, isMouthGesturesEnabled]);

  useEffect(() => {
    const settings = getUserSettings();
    if (settings.controlMethod) {
      setSelectedMethod(settings.controlMethod);
    }
    setUserGender(settings.gender);
  }, []);
  
  // Gender-aware translations
  const getGenderedTitle = () => {
    if (userGender === "female") return t("controlMethodsTitleFemale" as any) || t("controlMethodsTitle");
    if (userGender === "male") return t("controlMethodsTitleMale" as any) || t("controlMethodsTitle");
    return t("controlMethodsTitle");
  };
  
  const getGenderedSubtitle = () => {
    if (userGender === "female") return t("controlMethodsSubtitleFemale" as any) || t("controlMethodsSubtitle");
    if (userGender === "male") return t("controlMethodsSubtitleMale" as any) || t("controlMethodsSubtitle");
    return t("controlMethodsSubtitle");
  };

  const handleSelect = useCallback((methodId: string) => {
    setSelectedMethod(methodId);
    const settings = getUserSettings();
    saveUserSettings({ ...settings, controlMethod: methodId });
    
    // Dispatch event to notify all listeners about control method change
    window.dispatchEvent(new CustomEvent('controlMethodChanged', { 
      detail: { method: methodId } 
    }));
    
    // For standard pointer, just go back home - no additional settings needed
    if (methodId === "standard_pointer") {
      navigate("/");
      return;
    }
    
    // Navigate to the appropriate settings page for other control methods
    const methodRoutes: Record<string, string> = {
      eye_control: "/control/eye-control",
      external_switch: "/control/external-switch",
      voice: "/control/voice",
      gaze_tracking: "/control/gaze-tracking",
      head_tracking: "/control/head-tracking",
      mouth_gestures: "/control/mouth-gestures",
    };
    
    navigate(methodRoutes[methodId] || "/");
  }, [navigate]);

  const getRowGridConfig = useCallback((len: number) => {
    // Tailwind-safe class maps (no dynamic string interpolation)
    const mdFirstItemStartByLen: Record<number, string> = {
      1: "md:col-start-3",
      2: "md:col-start-2",
      3: "md:col-start-1",
      4: "md:col-start-1",
    };

    // On md+ we use a 6-col grid for rows with 1–3 items so we can center perfectly
    // (each card spans 2 columns; first item starts at 3/2/1 respectively)
    if (len >= 1 && len <= 3) {
      return {
        containerClassName: "md:grid-cols-6",
        itemClassName: "md:col-span-2",
        firstItemClassName: mdFirstItemStartByLen[len] ?? "md:col-start-1",
      };
    }

    // Default: 4 items (classic 4-col layout)
    return {
      containerClassName: "md:grid-cols-4",
      itemClassName: "md:col-span-1",
      firstItemClassName: "md:col-start-1",
    };
  }, []);

  return (
    <MainLayout>
      <div 
        className="flex-1 flex flex-col items-center justify-center p-4 bg-background h-full"
        dir={dir}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="text-2xl font-bold text-foreground mb-2">
            {getGenderedTitle()}
          </h1>
          <p className="text-muted-foreground text-lg">
            {getGenderedSubtitle()}
          </p>
        </motion.div>

        <div className="flex flex-col gap-4 w-full max-w-6xl px-4">
          {(() => {
            const row0 = controlMethods.slice(0, 4);
            const row1 = controlMethods.slice(4);

            const row0Config = getRowGridConfig(row0.length);
            const row1Config = getRowGridConfig(row1.length);

            return (
              <>
                {/* Row 0 */}
                <div
                  className={cn(
                    "grid grid-cols-2 gap-4 rounded-xl p-2",
                    row0Config.containerClassName
                  )}
                >
                  {row0.map((method, index) => {
                    const Icon = method.icon;
                    const isSelected = selectedMethod === method.id;

                    return (
                      <motion.button
                        key={method.id}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: index * 0.05 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSelect(method.id);
                        }}
                        className={cn(
                          "flex flex-col items-center justify-center gap-4 p-6 rounded-2xl transition-all cursor-pointer",
                          "border-2",
                          "focus:outline-none focus:ring-0",
                          isSelected
                            ? "bg-primary text-primary-foreground border-primary"
                            : "bg-card border-border hover:border-primary/50",
                          "w-full aspect-square",
                          row0Config.itemClassName,
                          index === 0 ? row0Config.firstItemClassName : undefined,
                          // Base centering for a single item on small screens
                          row0.length === 1 ? "col-span-2" : undefined
                        )}
                      >
                        {/* Icon */}
                        <div className="w-16 h-16 flex items-center justify-center transition-colors">
                          {method.compositeIcons ? (
                            <div className="flex items-center gap-2">
                              {method.compositeIcons.map((CompositeIcon, i) => (
                                <CompositeIcon
                                  key={i}
                                  className={cn(
                                    "w-10 h-10",
                                    isSelected ? "text-primary-foreground" : "text-primary"
                                  )}
                                />
                              ))}
                            </div>
                          ) : (
                            <Icon className={cn("w-12 h-12", isSelected ? "text-primary-foreground" : "text-primary")} />
                          )}
                        </div>

                        {/* Text Content */}
                        <div className="text-center">
                          <h3
                            className={cn(
                              "font-bold text-xl leading-tight",
                              isSelected ? "text-primary-foreground" : "text-foreground"
                            )}
                          >
                            {t(`control_${method.id}_title` as any)}
                          </h3>
                          <p
                            className={cn(
                              "text-sm mt-2 leading-tight px-2",
                              isSelected ? "text-primary-foreground/80" : "text-muted-foreground"
                            )}
                          >
                            {t(`control_${method.id}_desc` as any)}
                          </p>
                        </div>
                      </motion.button>
                    );
                  })}
                </div>

                {/* Row 1 (only if needed) */}
                {row1.length > 0 && (
                  <div
                    className={cn(
                      "grid grid-cols-2 gap-4 rounded-xl p-2",
                      row1Config.containerClassName
                    )}
                  >
                    {row1.map((method, idx) => {
                      const index = idx + 4;
                      const Icon = method.icon;
                      const isSelected = selectedMethod === method.id;

                      return (
                        <motion.button
                          key={method.id}
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: index * 0.05 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelect(method.id);
                          }}
                          className={cn(
                            "flex flex-col items-center justify-center gap-4 p-6 rounded-2xl transition-all cursor-pointer",
                            "border-2",
                            "focus:outline-none focus:ring-0",
                            isSelected
                              ? "bg-primary text-primary-foreground border-primary"
                              : "bg-card border-border hover:border-primary/50",
                            "w-full aspect-square",
                            row1Config.itemClassName,
                            idx === 0 ? row1Config.firstItemClassName : undefined,
                            // Base centering for a single item on small screens
                            row1.length === 1 ? "col-span-2" : undefined
                          )}
                        >
                          {/* Icon */}
                          <div className="w-16 h-16 flex items-center justify-center transition-colors">
                            {method.compositeIcons ? (
                              <div className="flex items-center gap-2">
                                {method.compositeIcons.map((CompositeIcon, i) => (
                                  <CompositeIcon
                                    key={i}
                                    className={cn(
                                      "w-10 h-10",
                                      isSelected ? "text-primary-foreground" : "text-primary"
                                    )}
                                  />
                                ))}
                              </div>
                            ) : (
                              <Icon className={cn("w-12 h-12", isSelected ? "text-primary-foreground" : "text-primary")} />
                            )}
                          </div>

                          {/* Text Content */}
                          <div className="text-center">
                            <h3
                              className={cn(
                                "font-bold text-xl leading-tight",
                                isSelected ? "text-primary-foreground" : "text-foreground"
                              )}
                            >
                              {t(`control_${method.id}_title` as any)}
                            </h3>
                            <p
                              className={cn(
                                "text-sm mt-2 leading-tight px-2",
                                isSelected ? "text-primary-foreground/80" : "text-muted-foreground"
                              )}
                            >
                              {t(`control_${method.id}_desc` as any)}
                            </p>
                          </div>
                        </motion.button>
                      );
                    })}
                  </div>
                )}
              </>
            );
          })()}
        </div>
      </div>
    </MainLayout>
  );
}
